import React, { useState, useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Link } from "react-router-dom";
import apiService, { getImageUrl, formatPrice } from "../../services/api";
import "./PopularDestination.css"; // Import our custom CSS

// Fallback destinations if API fails
const fallbackDestinations = [
    {
        id: 1,
        title: "Greece Tour Package",
        image: "/assets/img/tour/tour_2_1.jpg",
        rating: 4.8,
        price: 980,
        duration: "7 Days",
        link: "/tour-details",
        location: "Athens, Greece",
        difficulty: "Easy",
        category: "Cultural"
    },
    {
        id: 2,
        title: "Italy Tour Package", 
        image: "/assets/img/tour/tour_2_2.jpg",
        rating: 4.8,
        price: 980,
        duration: "7 Days",
        link: "/tour-details",
        location: "Rome, Italy",
        difficulty: "Moderate",
        category: "Cultural"
    },
    {
        id: 3,
        title: "Dubai Tour Package",
        image: "/assets/img/tour/tour_2_3.jpg",
        rating: 4.8,
        price: 980,
        duration: "7 Days",
        link: "/tour-details",
        location: "Dubai, UAE",
        difficulty: "Easy",
        category: "Luxury"
    },
    {
        id: 4,
        title: "Japan Adventure",
        image: "/assets/img/tour/tour_2_1.jpg",
        rating: 4.9,
        price: 1200,
        duration: "10 Days",
        link: "/tour-details",
        location: "Tokyo, Japan",
        difficulty: "Moderate",
        category: "Adventure"
    },
    {
        id: 5,
        title: "Switzerland Alps",
        image: "/assets/img/tour/tour_2_2.jpg",
        rating: 4.7,
        price: 1500,
        duration: "8 Days",
        link: "/tour-details",
        location: "Zurich, Switzerland", 
        difficulty: "Hard",
        category: "Mountain"
    },
];

const sectionStyle = {
    position: "relative",
    overflow: "hidden",
    backgroundImage: "url('/assets/img/bg/tour_bg_2.jpg')",
    backgroundPosition: "top center",
    zIndex: 3,
    backgroundRepeat: "no-repeat",
    marginBottom: -348,
};

function PopularDestination() {
    const [destinations, setDestinations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const swiperRef = useRef(null);

    // Fetch featured destinations from API
    useEffect(() => {
        const fetchFeaturedDestinations = async () => {
            try {
                setLoading(true);
                console.log('Fetching featured destinations...');
                
                // Fetch featured destinations using dedicated endpoint
                const response = await apiService.getFeaturedDestinations();
                console.log('API response:', response);
                
                // Map API data to component format
                const mappedDestinations = response.results?.map(dest => {
                    const mappedDest = {
                        id: dest.id,
                        title: dest.name,
                        image: getImageUrl(dest.main_image) || '/assets/img/tour/tour_2_1.jpg', // fallback image
                        rating: parseFloat(dest.rating) || 0,
                        price: parseFloat(dest.price) || 0,
                        duration: `${dest.duration_days} Days`,
                        link: `/destination/${dest.slug}`,
                        description: dest.short_description,
                        location: `${dest.city}, ${dest.country}`,
                        difficulty: dest.difficulty,
                        category: dest.category?.name
                    };
                    console.log('Mapped destination:', mappedDest);
                    return mappedDest;
                }) || [];
                
                console.log('All featured destinations loaded:', mappedDestinations);
                setDestinations(mappedDestinations);
                setError(null);
                
            } catch (error) {
                console.error('Error fetching featured destinations:', error);
                setError(error.message);
                
                // Use fallback data if API fails
                setDestinations(fallbackDestinations);
            } finally {
                setLoading(false);
            }
        };

        fetchFeaturedDestinations();
    }, []);

    if (loading) {
        return (
            <section className="destination-area position-relative overflow-hidden bg-top-center z-index-3 space-top" style={sectionStyle}>
                <div className="container">
                    <div className="destination-header">
                        <div className="title-area text-center">
                            <span className="sub-title">Best Recommended Places</span>
                            <h2 className="sec-title">Loading Popular Destinations...</h2>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section
            className="destination-area position-relative overflow-hidden bg-top-center z-index-3 space-top"
            id="destination-sec"
            style={sectionStyle}
        >            
            <div className="container">
                {/* Enhanced Header with inline View All button */}
                <div className="destination-header">
                    <div className="row align-items-center">
                        <div className="col-lg-8 col-md-7">
                            <div className="title-area text-left">
                                <span className="sub-title">Top Destination</span>
                                <h2 className="sec-title mb-0">Popular Destinations</h2>
                                <p className="destination-text mt-2">
                                    Discover breathtaking destinations and unforgettable adventures around the world.
                                </p>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-5">
                            <div className="destination-actions text-center">
                                <Link to="/destination" className="view-all-btn">
                                    View All Destinations
                                    <i className="fa-light fa-arrow-right ms-2"></i>
                                </Link>
                            </div>
                        </div>
                    </div>
                    
                    {/* Navigation Arrows */}
                    <div className="destination-nav">
                        <button 
                            className="destination-arrow destination-prev"
                            onClick={() => swiperRef.current?.slidePrev()}
                            aria-label="Previous destinations"
                        >
                            <i className="fa-light fa-arrow-left"></i>
                        </button>
                        <button 
                            className="destination-arrow destination-next"
                            onClick={() => swiperRef.current?.slideNext()}
                            aria-label="Next destinations"
                        >
                            <i className="fa-light fa-arrow-right"></i>
                        </button>
                    </div>
                </div>

                <div className="row justify-content-center">
                    <div className="col-12">
                        <div className="slider-area destination-slider">
                            <Swiper
                                modules={[Navigation, Pagination, Autoplay]}
                                onSwiper={(swiper) => {
                                    swiperRef.current = swiper;
                                }}
                                breakpoints={{
                                    0: { slidesPerView: 1, spaceBetween: 20 },
                                    576: { slidesPerView: 2, spaceBetween: 24 },
                                    768: { slidesPerView: 3, spaceBetween: 30 },
                                    992: { slidesPerView: 3, spaceBetween: 30 },
                                }}
                                grabCursor={true}
                                centeredSlides={false}
                                loop={destinations.length > 3}
                                speed={800}
                                autoplay={{
                                    delay: 4500,
                                    disableOnInteraction: false,
                                    pauseOnMouseEnter: true,
                                }}
                                navigation={{
                                    prevEl: '.destination-prev',
                                    nextEl: '.destination-next',
                                }}
                                pagination={{ 
                                    clickable: true,
                                    dynamicBullets: true 
                                }}
                                className="centered-destination-slider th-slider has-shadow"
                            >
                                {destinations.length > 0 ? destinations.map((destination) => (
                                    <SwiperSlide key={destination.id}>
                                        <div className="destination-card th-ani gsap-cursor">
                                            <div className="destination-card_img global-img">
                                                <img 
                                                    src={destination.image} 
                                                    alt={destination.title}
                                                    loading="lazy"
                                                    onError={(e) => {
                                                        e.target.src = '/assets/img/tour/tour_2_1.jpg'; // fallback image
                                                    }}
                                                />
                                                {/* Overlay with difficulty badge */}
                                                <div className="destination-overlay">
                                                    <div className="difficulty-badge">
                                                        <i className="fa-light fa-signal"></i>
                                                        {destination.difficulty}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="destination-content">
                                                <h3 className="destination-title">
                                                    <Link to={destination.link}>{destination.title}</Link>
                                                </h3>
                                                
                                                {/* Location */}
                                                <div className="destination-location">
                                                    <i className="fa-light fa-map-marker-alt"></i>
                                                    {destination.location}
                                                </div>
                                                
                                                {/* Rating */}
                                                <div className="destination-rating">
                                                    <div className="star-rating" role="img" aria-label={`Rated ${destination.rating} out of 5`}>
                                                        <span style={{ width: `${(destination.rating / 5) * 100}%` }}></span>
                                                    </div>
                                                    <span className="rating-text">
                                                        ({destination.rating.toFixed(1)} Rating)
                                                    </span>
                                                </div>
                                                
                                                {/* Price */}
                                                <h4 className="destination-price">
                                                    <span className="currency">${destination.price.toFixed(0)}</span>
                                                    <span className="period">/Person</span>
                                                </h4>
                                                
                                                {/* Action Section */}
                                                <div className="destination-action">
                                                    <span className="destination-duration">
                                                        <i className="fa-light fa-clock"></i>
                                                        {destination.duration}
                                                    </span>
                                                    {destination.category && (
                                                        <span className="category-tag">
                                                            <i className="fa-light fa-tag"></i>
                                                            {destination.category}
                                                        </span>
                                                    )}
                                                </div>
                                                
                                                {/* Explore Button */}
                                                <Link to={destination.link} className="th-btn style4 th-icon destination-btn">
                                                    Explore Now
                                                </Link>
                                            </div>
                                        </div>
                                    </SwiperSlide>
                                )) : (
                                    <SwiperSlide>
                                        <div className="destination-card th-ani destination-empty">
                                            <div className="destination-content text-center">
                                                <h3>No Featured Destinations Available</h3>
                                                <p>Please mark some destinations as featured in the admin panel to display them here.</p>
                                            </div>
                                        </div>
                                    </SwiperSlide>
                                )}
                            </Swiper>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default PopularDestination;
